const onboardingStorageKey = 'istoryline_onboarding_v1'

const onboardingChecklistItems = [
  {
    id: 'tour_visualizationBasics_completed',
    label: 'Complete the visualization basics tour',
  },
  {
    id: 'authoringStarted',
    label: 'Turn on Author mode',
  },
  {
    id: 'snippetSaved',
    label: 'Save or draft a story snippet',
  },
  {
    id: 'aiInsightOpened',
    label: 'Open AI Story Insight',
  },
  {
    id: 'annotationReuseTouched',
    label: 'Try layers, import, or export',
  },
]

export function initOnboardingGuide(documentRef = document) {
  const guidePanel = documentRef.getElementById('guidePanel')
  const guideBtn = documentRef.getElementById('guideBtn')
  const guideCloseBtn = documentRef.getElementById('guideCloseBtn')
  const guideDismissWelcomeBtn = documentRef.getElementById(
    'guideDismissWelcomeBtn'
  )
  const guideTabs = Array.from(documentRef.querySelectorAll('[data-guide-tab]'))
  const guideSections = Array.from(
    documentRef.querySelectorAll('[data-guide-section]')
  )
  const guideTourButtons = Array.from(
    documentRef.querySelectorAll('[data-guide-start-tour]')
  )
  const coachOverlay = documentRef.getElementById('coachMarkOverlay')
  const coachTargetFrame = documentRef.getElementById('coachTargetFrame')
  const coachCard = documentRef.getElementById('coachCard')
  const coachStepCount = documentRef.getElementById('coachStepCount')
  const coachTitle = documentRef.getElementById('coachTitle')
  const coachBody = documentRef.getElementById('coachBody')
  const coachBackBtn = documentRef.getElementById('coachBackBtn')
  const coachSkipBtn = documentRef.getElementById('coachSkipBtn')
  const coachNextBtn = documentRef.getElementById('coachNextBtn')
  const guideChecklist = documentRef.getElementById('guideChecklist')

  let activeGuideTourId = null
  let activeGuideTourStepIndex = 0
  let activeGuideTourRequest = 0

  function readOnboardingState() {
    try {
      return JSON.parse(
        window.localStorage.getItem(onboardingStorageKey) || '{}'
      )
    } catch (error) {
      return {}
    }
  }

  function writeOnboardingState(patch) {
    const current = readOnboardingState()
    window.localStorage.setItem(
      onboardingStorageKey,
      JSON.stringify({
        version: 1,
        ...current,
        ...patch,
        lastUpdated: new Date().toISOString(),
      })
    )
  }

  function getOnboardingChecklistState() {
    return readOnboardingState().checklist || {}
  }

  function markOnboardingChecklistItem(itemId) {
    if (!itemId) return
    const checklist = getOnboardingChecklistState()
    if (checklist[itemId]) return
    writeOnboardingState({
      checklist: {
        ...checklist,
        [itemId]: true,
      },
    })
    renderOnboardingChecklist()
  }

  function renderOnboardingChecklist() {
    if (!guideChecklist) return
    const state = readOnboardingState()
    const checklist = state.checklist || {}
    guideChecklist.innerHTML = ''
    onboardingChecklistItems.forEach(item => {
      const row = documentRef.createElement('div')
      row.className = 'guide-checklist-row'
      const isComplete = Boolean(checklist[item.id] || state[item.id])
      row.classList.toggle('is-complete', isComplete)
      row.textContent = item.label
      guideChecklist.appendChild(row)
    })
  }

  function setGuideSection(sectionId) {
    guideTabs.forEach(tab => {
      tab.classList.toggle(
        'is-active',
        tab.getAttribute('data-guide-tab') === sectionId
      )
    })
    guideSections.forEach(section => {
      section.classList.toggle(
        'is-active',
        section.getAttribute('data-guide-section') === sectionId
      )
    })
  }

  function openGuide(sectionId = 'welcome') {
    if (!guidePanel) return
    renderOnboardingChecklist()
    setGuideSection(sectionId)
    guidePanel.style.display = 'block'
  }

  function closeGuide(markWelcomeDismissed = true) {
    if (!guidePanel) return
    guidePanel.style.display = 'none'
    if (markWelcomeDismissed) {
      writeOnboardingState({ welcomeDismissed: true })
    }
  }

  function isTourTargetVisible(target) {
    if (!target || !target.getBoundingClientRect) return false
    const style = window.getComputedStyle(target)
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false
    }
    const rect = target.getBoundingClientRect()
    return rect.width > 0 || rect.height > 0
  }

  function ensureAuthoringPanelOpen() {
    const panel = documentRef.getElementById('authoringPanel')
    if (isTourTargetVisible(panel)) return
    const button = documentRef.getElementById('authorModeBtn')
    if (button) button.click()
    markOnboardingChecklistItem('authoringStarted')
    window.setTimeout(() => {
      if (panel && !isTourTargetVisible(panel)) {
        panel.style.display = 'block'
      }
    }, 80)
  }

  function ensureAIInsightPanelOpen() {
    const panel = documentRef.getElementById('aiInsightPanel')
    if (panel) panel.style.display = 'block'
    markOnboardingChecklistItem('aiInsightOpened')
  }

  const guideTours = {
    visualizationBasics: [
      {
        selector: '#showSynopsisBtn',
        placement: 'bottom',
        title: 'Story Context',
        body:
          'Open the synopsis when you need the Sims challenge premise behind the timeline.',
      },
      {
        selector: '#mySvg',
        placement: 'right',
        title: 'Character Storylines',
        body:
          'Each colored path follows one Sim through the story. Read left to right to follow chronological movement and interaction.',
      },
      {
        selector: '.event-icon-group',
        placement: 'right',
        title: 'Event Icons',
        body:
          'Icons mark gameplay events. Hover them to inspect descriptions, involved Sims, and video details when a clip exists.',
        timeout: 12000,
      },
      {
        selector: '#resetZoomBtn',
        placement: 'bottom',
        title: 'Canvas Controls',
        body:
          'Use zoom, reset, and hand mode to move through dense sections of the storyline.',
      },
      {
        selector: '#filterPanel',
        placement: 'top',
        title: 'Event Filters',
        body:
          'The bottom panel works as both a filter and legend. Toggle categories to reduce visual density while reading.',
      },
    ],
    authoringWorkflow: [
      {
        selector: '#authorModeBtn',
        placement: 'bottom',
        title: 'Turn On Authoring',
        body:
          'Author mode turns event icons into selectable evidence for a story snippet.',
      },
      {
        selector: '.event-icon-group',
        placement: 'right',
        title: 'Pick Evidence Events',
        body:
          'Select a small set of event icons that belong together. These selected events become the evidence for the band.',
        setup: ensureAuthoringPanelOpen,
        timeout: 12000,
      },
      {
        selector: '#authoringPanel',
        placement: 'left',
        title: 'Story Authoring Panel',
        body:
          'The side panel records your selected evidence, layer, draft label, summary, and color before saving.',
        setup: ensureAuthoringPanelOpen,
      },
      {
        selector: '#summarizeSelectionBtn',
        placement: 'left',
        title: 'AI Summarize',
        body:
          'After selecting events, AI Summarize drafts a story-like label and summary that you can revise.',
        setup: ensureAuthoringPanelOpen,
      },
      {
        selector: '#storyDraftSummary',
        placement: 'left',
        title: 'Edit The Draft',
        body:
          'The generated summary is editable. Keep the story tone but correct anything that does not match your evidence.',
        setup: ensureAuthoringPanelOpen,
      },
      {
        selector: '#saveDraftBtn',
        placement: 'left',
        title: 'Save A Snippet',
        body:
          'Saving creates a highlight band in the visualization and preserves the evidence events for audit.',
        setup: ensureAuthoringPanelOpen,
      },
      {
        selector: '#savedSnippetsList',
        placement: 'left',
        title: 'Review Saved Snippets',
        body:
          'Saved snippets stay clickable. Selecting one navigates back to its corresponding band and evidence.',
        setup: ensureAuthoringPanelOpen,
      },
    ],
    aiInsightWorkflow: [
      {
        selector: '#aiStoryInsightBtn',
        placement: 'bottom',
        title: 'AI Story Insight',
        body:
          'This separate button asks AI to propose candidate story arcs from the full event data set.',
      },
      {
        selector: '#aiInsightPanel',
        placement: 'right',
        title: 'Insight Window',
        body:
          'Insights appear in a movable in-app window so you can compare suggestions with the visualization.',
        setup: ensureAIInsightPanelOpen,
      },
      {
        selector: '#aiInsightRefreshBtn',
        placement: 'right',
        title: 'Refresh Suggestions',
        body:
          'Refresh requests another batch. This keeps exploration separate from accepting a specific snippet.',
        setup: ensureAIInsightPanelOpen,
      },
      {
        selector: '#aiInsightList',
        placement: 'right',
        title: 'Inspect Before Accepting',
        body:
          'Each suggestion can be shown, edited, accepted, or rejected. Accepted insights become saved snippets.',
        setup: ensureAIInsightPanelOpen,
      },
    ],
    reuseWorkflow: [
      {
        selector: '#authoringLayerSelect',
        placement: 'left',
        title: 'Annotation Layers',
        body:
          'Layers let you separate different readings of the same story or compare annotation passes.',
        setup: ensureAuthoringPanelOpen,
      },
      {
        selector: '#addAuthoringLayerBtn',
        placement: 'left',
        title: 'Create A Layer',
        body:
          'New layer starts a clean annotation space without deleting the snippets in other layers.',
        setup: ensureAuthoringPanelOpen,
      },
      {
        selector: '#downloadAnnotationsBtn',
        placement: 'left',
        title: 'Download JSON',
        body:
          'Download JSON exports snippets, evidence, layers, AI decisions, and provenance for reuse or analysis.',
        setup: ensureAuthoringPanelOpen,
      },
      {
        selector: '#importAnnotationsBtn',
        placement: 'left',
        title: 'Import JSON',
        body:
          'Import JSON restores a previous annotation file into the browser-local workspace.',
        setup: ensureAuthoringPanelOpen,
      },
    ],
  }

  function getTourTarget(selector) {
    if (!selector) return null
    return documentRef.querySelector(selector)
  }

  function waitForTourTarget(step) {
    const timeout = step.timeout || 3000
    const startedAt = Date.now()
    return new Promise(resolve => {
      function check() {
        const target = getTourTarget(step.selector)
        if (target && isTourTargetVisible(target)) {
          resolve(target)
          return
        }
        if (Date.now() - startedAt >= timeout) {
          resolve(null)
          return
        }
        window.setTimeout(check, 160)
      }
      check()
    })
  }

  function getFallbackTourRect() {
    return {
      left: 24,
      top: 72,
      right: 24,
      bottom: 72,
      width: 0,
      height: 0,
    }
  }

  function getSafeTourRect(target) {
    if (!target || !target.getBoundingClientRect) {
      return getFallbackTourRect()
    }
    const rect = target.getBoundingClientRect()
    if (!Number.isFinite(rect.left) || !Number.isFinite(rect.top)) {
      return getFallbackTourRect()
    }
    return rect
  }

  function clampGuideValue(value, minValue, maxValue) {
    return Math.min(Math.max(value, minValue), maxValue)
  }

  function placeCoachElements(rect, placement) {
    const inset = 8
    const viewportWidth = window.innerWidth || documentRef.body.clientWidth
    const viewportHeight = window.innerHeight || documentRef.body.clientHeight
    const targetBoxLeft = Math.max(inset, rect.left - inset)
    const targetBoxTop = Math.max(inset, rect.top - inset)
    const targetBoxWidth = Math.min(
      viewportWidth - targetBoxLeft - inset,
      Math.max(28, rect.width + inset * 2)
    )
    const targetBoxHeight = Math.min(
      viewportHeight - targetBoxTop - inset,
      Math.max(28, rect.height + inset * 2)
    )

    if (coachTargetFrame) {
      coachTargetFrame.style.left = targetBoxLeft + 'px'
      coachTargetFrame.style.top = targetBoxTop + 'px'
      coachTargetFrame.style.width = targetBoxWidth + 'px'
      coachTargetFrame.style.height = targetBoxHeight + 'px'
    }

    if (!coachCard) return
    const cardWidth = Math.min(320, Math.max(260, viewportWidth - 32))
    const cardHeight = coachCard.offsetHeight || 180
    let cardLeft = rect.left
    let cardTop = rect.bottom + 16

    if (placement === 'right') {
      cardLeft = rect.right + 16
      cardTop = rect.top
    } else if (placement === 'left') {
      cardLeft = rect.left - cardWidth - 16
      cardTop = rect.top
    } else if (placement === 'top') {
      cardLeft = rect.left
      cardTop = rect.top - cardHeight - 16
    }

    cardLeft = clampGuideValue(cardLeft, 16, viewportWidth - cardWidth - 16)
    cardTop = clampGuideValue(cardTop, 16, viewportHeight - cardHeight - 16)
    coachCard.style.left = cardLeft + 'px'
    coachCard.style.top = cardTop + 'px'
  }

  async function renderGuideTourStep() {
    if (!activeGuideTourId || !guideTours[activeGuideTourId]) return
    const tour = guideTours[activeGuideTourId]
    const step = tour[activeGuideTourStepIndex]
    const requestId = activeGuideTourRequest + 1
    activeGuideTourRequest = requestId
    if (typeof step.setup === 'function') {
      step.setup()
    }

    if (coachOverlay) {
      coachOverlay.classList.add('is-active')
      coachOverlay.setAttribute('aria-hidden', 'false')
    }
    if (coachTitle) coachTitle.textContent = step.title
    if (coachBody) coachBody.textContent = 'Finding this part of the view...'
    if (coachStepCount) {
      coachStepCount.textContent =
        'Step ' + (activeGuideTourStepIndex + 1) + ' of ' + tour.length
    }
    if (coachBackBtn) {
      coachBackBtn.disabled = activeGuideTourStepIndex === 0
    }
    if (coachNextBtn) {
      coachNextBtn.textContent =
        activeGuideTourStepIndex === tour.length - 1 ? 'Done' : 'Next'
    }

    const target = await waitForTourTarget(step)
    if (requestId !== activeGuideTourRequest || !activeGuideTourId) return
    if (coachBody) coachBody.textContent = step.body
    placeCoachElements(getSafeTourRect(target), step.placement)
  }

  function startGuideTour(tourId) {
    if (!guideTours[tourId]) return
    const synopsisModalForTour = documentRef.getElementById(
      'storySynopsisModal'
    )
    if (synopsisModalForTour) synopsisModalForTour.style.display = 'none'
    closeGuide(false)
    activeGuideTourId = tourId
    activeGuideTourStepIndex = 0
    renderGuideTourStep()
  }

  function endGuideTour(completed) {
    if (completed && activeGuideTourId) {
      const patch = {}
      patch['tour_' + activeGuideTourId + '_completed'] = true
      writeOnboardingState(patch)
      if (activeGuideTourId === 'visualizationBasics') {
        markOnboardingChecklistItem('tour_visualizationBasics_completed')
      }
    }
    activeGuideTourRequest += 1
    activeGuideTourId = null
    activeGuideTourStepIndex = 0
    if (coachOverlay) {
      coachOverlay.classList.remove('is-active')
      coachOverlay.setAttribute('aria-hidden', 'true')
    }
    renderOnboardingChecklist()
  }

  function moveGuideTour(delta) {
    if (!activeGuideTourId || !guideTours[activeGuideTourId]) return
    const tour = guideTours[activeGuideTourId]
    const nextIndex = activeGuideTourStepIndex + delta
    if (nextIndex < 0) return
    if (nextIndex >= tour.length) {
      endGuideTour(true)
      return
    }
    activeGuideTourStepIndex = nextIndex
    renderGuideTourStep()
  }

  guideTabs.forEach(tab => {
    tab.addEventListener('click', () =>
      setGuideSection(tab.getAttribute('data-guide-tab'))
    )
  })
  documentRef.querySelectorAll('[data-guide-open-tab]').forEach(button => {
    button.addEventListener('click', () =>
      setGuideSection(button.getAttribute('data-guide-open-tab'))
    )
  })
  guideTourButtons.forEach(button => {
    button.addEventListener('click', () =>
      startGuideTour(button.getAttribute('data-guide-start-tour'))
    )
  })
  if (coachBackBtn) {
    coachBackBtn.addEventListener('click', () => moveGuideTour(-1))
  }
  if (coachSkipBtn) {
    coachSkipBtn.addEventListener('click', () => endGuideTour(false))
  }
  if (coachNextBtn) {
    coachNextBtn.addEventListener('click', () => moveGuideTour(1))
  }

  const checklistActionBindings = [
    ['authorModeBtn', 'authoringStarted'],
    ['summarizeSelectionBtn', 'snippetSaved'],
    ['saveDraftBtn', 'snippetSaved'],
    ['aiStoryInsightBtn', 'aiInsightOpened'],
    ['addAuthoringLayerBtn', 'annotationReuseTouched'],
    ['downloadAnnotationsBtn', 'annotationReuseTouched'],
    ['importAnnotationsBtn', 'annotationReuseTouched'],
  ]
  checklistActionBindings.forEach(([elementId, checklistId]) => {
    const element = documentRef.getElementById(elementId)
    if (element) {
      element.addEventListener('click', () =>
        markOnboardingChecklistItem(checklistId)
      )
    }
  })

  window.addEventListener('resize', () => {
    if (activeGuideTourId) renderGuideTourStep()
  })
  if (guideBtn) guideBtn.addEventListener('click', () => openGuide('welcome'))
  if (guideCloseBtn) {
    guideCloseBtn.addEventListener('click', () => closeGuide(true))
  }
  if (guideDismissWelcomeBtn) {
    guideDismissWelcomeBtn.addEventListener('click', () => closeGuide(true))
  }
  documentRef.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return
    if (activeGuideTourId) {
      endGuideTour(false)
      return
    }
    if (guidePanel && guidePanel.style.display === 'block') {
      closeGuide(true)
    }
  })

  renderOnboardingChecklist()

  return {
    markOnboardingChecklistItem,
    readOnboardingState,
    startGuideTour,
    writeOnboardingState,
  }
}
